import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MediaSummary } from 'src/app/models';
import { CreatorTeamResponse } from 'src/app/models/creator-team-response';
import { CreatorTeamService } from 'src/app/services/creator-team.service';


@Component({
  selector: 'app-creator-profile',
  templateUrl: './creator-profile.component.html',
  styleUrls: ['./creator-profile.component.scss']
})
export class CreatorProfileComponent implements OnInit {

  constructor(
    private teamService: CreatorTeamService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
  }

  creatorTeam: CreatorTeamResponse | null = null;
  notFound: boolean = false;
  creatorSlug: string;
  destroy$ = new Subject<void>();
  noMoreVideos: boolean = false;
  currentPage: number = 1;
  currentTab: string = '';
  media: MediaSummary[] = [];
  displayYearFilter: boolean = false;
  isDescriptionExpanded: boolean = false;
  showReadMore: boolean = false;
  isSearchActive: boolean = false;
  isDropdownOpen: boolean = false;
  selectedFilter: string = '';
  loadingTab: boolean = false;
  focusedIndex: number = 0;
  isLoading: boolean = false;

  @ViewChild('descriptionElement') descriptionElement: ElementRef;
  @ViewChild('dropDownHeader') dropDownHeader: ElementRef;
  @ViewChild('followButton', { static: false }) followButtonComponent: any;

  ngOnInit(): void {
    this.creatorSlug = this.route.snapshot.paramMap.get('slug');

    if (this.creatorSlug) {
      this.teamService.getTeamBySlug(this.creatorSlug)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (data) => {
            this.creatorTeam = data;
            setTimeout(() => {
              this.checkDescriptionTruncation();
            }, 1);
            if (data.tabs && data.tabs.length > 0) {
              this.currentTab = data.tabs[0];
              this.currentPage = 1;
              this.noMoreVideos = false;
              this.teamService.getTeamVideos(data.id, data.tabs[0]).subscribe({
                next: (videoData) => {
                  this.displayYearFilter = videoData.displayYearFilter;
                  this.media = videoData.media;
                  if (videoData.media.length < 20) {
                    this.noMoreVideos = true;
                  }
                },
                error: (err) => console.error('Failed to load team videos:', err)
              });
            }
          },
          error: (err) => {
            if (err.status === 404) {
              this.router.navigate(['/not-found']);
            }
            console.error('Failed to load creator data:', err);
          },
          complete: () => console.log('Creator data fetched successfully.')
        });
    }
  }

  checkHeroImages(): boolean {
    const team = this.creatorTeam;
    if (team) {
      return team.images.some(image =>
        image.type === 'hero-mobile' || image.type === 'hero-desktop'
      );
    }
    return false;
  }

  toggleDescription(): void {
    this.isDescriptionExpanded = true;
    this.heroFocus();
  }


  checkDescriptionTruncation(): void {
    if (!this.descriptionElement) {
      return;
    }
    const element = this.descriptionElement.nativeElement;
    const computedStyle = window.getComputedStyle(element);
    const lineHeightStr = computedStyle.lineHeight;
    let lineHeight = 0;
    if (lineHeightStr.endsWith('px')) {
      lineHeight = parseFloat(lineHeightStr);
    } else {
      lineHeight = 18;
    }
    const twoLineHeight = lineHeight * 2;
    const tolerance = 2;
    this.showReadMore = element.scrollHeight > (twoLineHeight + tolerance);
  }

  toggleSearch(): void {
    this.isSearchActive = !this.isSearchActive;
  }


  searchClick(): void {
    this.router.navigate(['/search'], {
      queryParams: {
        search: '',
        ctid: this.creatorTeam!.id,
        creatorName: this.creatorTeam!.displayName
      }
    });
  }

  toggleFilterDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  onTabClick(tab: string): void {
    this.loadingTab = true;
    this.currentTab = tab;
    this.selectedFilter = '';
    this.currentPage = 1;
    this.noMoreVideos = false;
    this.teamService.getTeamVideos(this.creatorTeam?.id, tab).subscribe({
      next: (videoData) => {
        this.media = videoData.media;
        this.loadingTab = false;
        if (videoData.media.length < 20) {
          this.noMoreVideos = true;
        }
      },
      error: (err) => {
        this.loadingTab = false;
        console.error('Failed to load team videos:', err)
      }
    });
  }

  thumbFocused(i: number) {
    this.focusedIndex = i;
  }

  loadMoreVideos(): void {
    if (this.isLoading || this.noMoreVideos) return;
    this.isLoading = true;
    this.currentPage = this.currentPage + 1;
    const pageToLoad = this.currentPage;
    this.teamService.getTeamVideos(this.creatorTeam?.id, this.currentTab, this.selectedFilter, pageToLoad)
      .subscribe({
        next: (videoData) => {
          if (videoData.media && videoData.media.length > 0) {
            this.media = [...this.media, ...videoData.media];
            if (videoData.media.length < 20) {
              const finalCheckPage = pageToLoad + 1;
              this.teamService.getTeamVideos(this.creatorTeam?.id, this.currentTab, this.selectedFilter, finalCheckPage)
                .subscribe({
                  next: (finalData) => {
                    if (finalData.media && finalData.media.length > 0) {
                      this.media = [...this.media, ...finalData.media];
                      this.currentPage = finalCheckPage;
                    } else {
                      this.noMoreVideos = true;
                    }
                  },
                  error: (err) => {
                    console.error('Failed to perform final check for team videos:', err);
                  },
                  complete: () => this.isLoading = false
                });
            } else {
              this.isLoading = false;
            }
          } else {
            this.noMoreVideos = true;
            this.isLoading = false;
          }
        },
        error: (err) => {
          console.error('Failed to load more team videos:', err);
          this.isLoading = false;
        }
      });
  }

  selectFilter(yearFilter: string): void {
    if (this.selectedFilter === yearFilter) {
      this.selectedFilter = '';
    } else {
      this.selectedFilter = yearFilter;
    }
    this.currentPage = 1;
    this.noMoreVideos = false;
    this.teamService.getTeamVideos(this.creatorTeam?.id, this.currentTab, this.selectedFilter)
      .subscribe({
        next: (videoData) => {
          this.media = videoData.media;
        },
        error: (err) => console.error('Failed to load team videos:', err)
      });

    this.isDropdownOpen = false;
    setTimeout(() => {
      this.dropDownHeader.nativeElement.focus();
    }, 1)
  }

  @ViewChild('scrollContainer') scrollContainer: ElementRef;

  onThumbFocused(i: number): void {
    this.focusedIndex = i;
    setTimeout(() => {
      const container = this.scrollContainer.nativeElement;
      const items = container.querySelectorAll('.col-12');
      if (items && items[i]) {
        (items[i] as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });

    if (i >= this.media.length - 8) {
      this.loadMoreVideos();
    }
  }

  heroFocus() {
    setTimeout(() => {
      this.followButtonComponent.focusButton();
    }, 1)
  }
}

